import { useEffect, useMemo, useRef, useState } from "react";
import { ReviewingGroup } from "../components/ReviewingGroup";
import { InviteClient } from "../components/InviteClient";
import { CoachSetupPrompt } from "../components/CoachSetupPrompt";
import { CopyLink } from "../components/CopyLink";
import { WeekLine } from "../components/WeekLine";
import { Workspace } from "../components/Shell";
import { supabase } from "../lib/supabase";
import { href, routeQuery } from "../lib/route";
import { displayName } from "../lib/roster";
import { calendarToday } from "../lib/days";
import { Avatar } from "../components/Avatar";
import { useQuerySort } from "../lib/useQuerySort";
import { EMPTY_MARK } from "../lib/emptyMark";
import { SeeExampleLink } from "../components/ExampleBanner";
import {
  boardRows, nextAssignedWord, notActiveCaption, trainedWord, type BoardInput,
} from "../lib/thisWeekBoard";
import {
  COLUMN, CHIP_LABEL, ROSTER_SORTS, SORT_LABEL, chipCounts, rosterChips, rosterFoot, rosterSortFrom, rosterView, rowWatchChip,
  type RosterChip, type RosterSort,
} from "../lib/rosterView";
import { watchByClient } from "../lib/reviewQueue";
import { useProposals, useRosterData } from "./useRosterData";
import { useReviewQueue } from "./useReviewQueue";
import { ActionStrip } from "../components/ActionStrip";
import { endCoaching, WITHDRAW_INVITATION_WARNING, type UpdatableClient } from "../lib/endCoaching";

/* CLIENTS — the roster, redrawn in our grammar (W72 step 1; Proposal 7's worked example, the p7 still).
 *
 * `Clients` · one search + chips + the sort · rows on the ground: monogram, name (+ `Watch N` when watched), THE WEEK
 * LINE, Trained, Next assigned,
 * actions on hover and on keyboard focus. Invited people are quieter rows with their own link.
 *
 * 🔴 W126 (his word, 2026-10-01: "WE DON'T NEED INFO IN THE ROW LIKE Get stronger · 6 days, OR Prone Hip Extension over
 * Box unlogged 2 weeks +2"): the goal · days line and the watch SENTENCE left the row. The goal and days are on the
 * client's Profile tab; the watch's words are the client page's `Watch` block, which the row's `Watch N` chip opens.
 *
 * ⚠️ WHAT WENT, AND WHERE ITS JOB WENT: the left radio rail (All · Connected · Pending · …) → the chips;
 * the checkbox column and bulk End coaching → the client page's own End coaching, one person at a time;
 * `Columns` and `Hide filters` → nothing to hide; the seven number columns → the week line here and the
 * Progress tab there; the Requests row → the account menu, with its count. Every one of those was a
 * signature of theirs (Proposal 7 rule 10).
 *
 * ⚠️ THE FIVE TESTS THAT PINNED THE COPIED TABLE WERE RETIRED WITH IT (`rosterCopy`, `rosterW19e`,
 * `rosterNextAssigned`, `phoneRoster`, `phoneRosterCopy`). W21's property — the phone-width roster
 * reads as the APP'S list — is met by the stacked row grammar: the 375 px still reads as the app's
 * list (Fable's ruling, 2026-09-25), so it is kept by the design rather than by a copied tree.
 *
 * ⚠️ THE DATA IS `useRosterData` — the SAME four reads and the SAME derivation the This week board draws,
 * so "Need you 2" here and "2 need you" there are one count, never two. */
export function Roster({ coachId }: { coachId: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);

  // ONE "today" per load: every row judged against the same day, across a midnight too.
  const [now] = useState(() => calendarToday());
  /* Bumped by a successful invite so the roster re-reads (a counter, not an effect on the state it sets). */
  const [reloadKey, setReloadKey] = useState(0);
  /* N2g — the pending row being withdrawn, and a refusal's own sentence. */
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const state = useRosterData(reloadKey, now);
  const data = state.status === "ready" ? state.data : null;
  const proposals = useProposals(data);

  const [chip, setChip] = useState<RosterChip>("all");
  const [query, setQuery] = useState("");
  /* THE SORT LIVES IN THE URL — a coach can link to a view — and is RE-READ on `hashchange`, because this
     screen does not remount on navigation (`useQuerySort`'s own reason). */
  const [sort, setSortState] = useQuerySort<RosterSort>(rosterSortFrom, routeQuery);
  function setSort(next: RosterSort) {
    setSortState(next);
    // `replaceState`: a sort is not a place, so Back still leaves the screen rather than un-sorting it.
    window.history.replaceState(null, "", `${href({ name: "roster" })}?sort=${next}`);
  }


  const rows = useMemo(() => {
    if (data === null) return [];
    const inputs: BoardInput[] = data.rows.map((r) => {
      const invited = data.invited[r.linkId] ?? { firstName: null, lastName: null, email: null };
      const name = displayName(r, invited);
      return {
        linkId: r.linkId,
        clientId: r.clientId,
        status: r.status,
        name,
        firstName: (r.firstName ?? invited.firstName ?? name).trim().split(/\s+/)[0] ?? name,
        invitedISO: r.invitedISO,
        endedISO: r.endedISO ?? null,
        lastTrainedISO: r.lastTrainedISO,
        firstAfterWeekISO: data.firstAfterWeekISO[r.linkId] ?? null,
        cells: data.weeks[r.linkId],
        changesProposed: proposals[r.linkId] ?? null,
      };
    });
    return boardRows(inputs);
  }, [data, proposals]);

  /* W101b — THE TWO-WEEK WATCH on the client's row. Its facts are the review queue's finished days (this week and last),
     read through the SAME hook the sidebar's badge and the Dashboard read (`useReviewQueue` — one query shape, never a
     second). ⚠️ It costs the queue's reads on this page too (the hooks are not a shared store); a failed or pending read
     draws no watch line and no `Watch` chip — never a guessed one. */
  const queueState = useReviewQueue(data, proposals, now);
  const watch = useMemo(() => (queueState.status === "ready" ? watchByClient(queueState.queue) : new Map<string, never[]>()), [queueState]);
  const watched = useMemo(() => new Set(watch.keys()), [watch]);
  const counts = chipCounts(rows, watched);
  // A chip that vanished (nobody watched any more) is not a filter left standing on an empty page.
  const activeChip: RosterChip = chip === "watch" && counts.watch === 0 ? "all" : chip;
  const shown = rosterView(rows, activeChip, query, sort, watched);
  // The day line (`Wednesday 14 Oct · 4 need you: Alex has nothing after Sunday · …`) went with William's first look
  // (10-14: *"Not sure if you need the '4 need you' panel? Looks messy"*; Fable 10-15): it said the Dashboard's To do again in
  // prose on a page whose rows and `Need you` chip already carry the facts — one count, never two (W111).

  return (
    <Workspace>
      <main id="main" className="roster-page ours-page">
        <header className="ours-head">
          <div className="ours-head-text">
            <h1 className="ht-title ours-title" ref={heading} tabIndex={-1}>Clients</h1>
          </div>
          <InviteClient coachId={coachId} onInvited={() => setReloadKey((n) => n + 1)} />
        </header>

        <CoachSetupPrompt coachId={coachId} />

        {state.status === "loading" && <p className="ht-callout ours-quiet" aria-live="polite">Loading your clients…</p>}
        {state.status === "error" && (
          <div role="alert" className="ours-error">
            <p className="ht-body">Could not load your clients.</p>
            <p className="ht-caption">{state.message}</p>
          </div>
        )}

        {state.status === "ready" && (
          <>
            <div className="ours-controls">
              <label className="ours-search">
                <span className="ht-visually-hidden">Search clients</span>
                <input className="ht-input" type="search" placeholder="Search clients…"
                       value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
              </label>
              {/* RULE 6 — chips live in the filter row and nowhere else; the chosen one is ink, never
                  the accent (the accent is a line, not a fill). */}
              <div className="ours-chips" role="group" aria-label="Show">
                {rosterChips(counts).map((c) => (
                  <button key={c} type="button" className="ours-chip" aria-pressed={activeChip === c}
                          onClick={() => setChip(c)}>
                    {CHIP_LABEL[c]} <span className="ours-chip-count">{counts[c]}</span>
                  </button>
                ))}
              </div>
              <label className="ours-sort">
                <span>Sorted by</span>
                <select value={sort} onChange={(e) => setSort(e.currentTarget.value as RosterSort)}>
                  {ROSTER_SORTS.map((s) => <option key={s} value={s}>{SORT_LABEL[s]}</option>)}
                </select>
              </label>
            </div>

            {rows.length === 0 && (
              <p className="ht-callout ours-quiet">No clients yet. Add client makes a link for one person — they join with it, no email needed.<SeeExampleLink /></p>
            )}
            {rows.length > 0 && shown.length === 0 && (
              <p className="ht-callout ours-quiet">Nobody matches.</p>
            )}

            {shown.length > 0 && (
              <table className="ours-rows roster-rows">
                <caption className="ht-visually-hidden">Your clients</caption>
                <thead>
                  <tr>
                    <th scope="col" className="ours-col-label">{COLUMN.client}</th>
                    <th scope="col" className="ours-col-label">{COLUMN.week}</th>
                    <th scope="col" className="ours-col-label ours-num">{COLUMN.trained}</th>
                    <th scope="col" className="ours-col-label ours-num">{COLUMN.next}</th>
                    <th scope="col"><span className="ht-visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const profile = data?.profile[r.linkId];
                    const next = nextAssignedWord(r.firstAfterWeekISO);
                    return (
                      <tr key={r.linkId} className={r.invited ? "ours-row is-invited" : "ours-row"}>
                        <th scope="row" className="ours-who-cell"><div className="ours-who">
                          <Avatar name={r.name} photoUrl={profile?.photoUrl ?? null} className="ours-disc" />
                          <span className="ours-names">
                            <span className="ours-name-line">
                              {r.clientId !== null
                                ? <a className="ours-name" href={href({ name: "client", clientId: r.clientId })}>{r.name}</a>
                                : <span className="ours-name">{r.name}</span>}
                              {/* W126 — a watched row's ONE mark: `Watch N` (its watched movements), opening the client's
                                  page, whose Week tab lists them. An unwatched row draws nothing here. */}
                              {r.clientId !== null && rowWatchChip((watch.get(r.clientId) ?? []).length) !== null && (
                                <a className="ours-watch-chip" href={href({ name: "client", clientId: r.clientId })}
                                   aria-label={`${rowWatchChip((watch.get(r.clientId) ?? []).length) ?? ""} — ${r.name}`}>{rowWatchChip((watch.get(r.clientId) ?? []).length)}</a>
                              )}
                            </span>
                            {r.invited && <span className="ours-caption">{notActiveCaption(r)}</span>}
                          </span>
                        </div></th>
                        <td className="ours-week">
                          {r.cells !== undefined
                            ? <WeekLine cells={r.cells} label={`${r.name}'s week`} />
                            : r.archived ? EMPTY_MARK : <span className="ours-caption">Nothing reaches them until they join</span>}
                        </td>
                        <td className="ours-num ours-trained">{r.invited ? EMPTY_MARK : trainedWord(r.lastTrainedISO, now)}</td>
                        <td className={`ours-num ours-next${!r.invited && next.needsAction ? " is-needed" : ""}`}>
                          {r.archived ? EMPTY_MARK : r.invited ? <CopyLink link={data?.inviteLink[r.linkId] ?? null} name={r.name} /> : next.text}
                        </td>
                        <td className="ours-actions">
                          {/* W119: the ONE `ActionStrip` — all three go somewhere, so all three are words. */}
                          {(r.clientId !== null || (r.invited && !r.archived)) && (
                            <ActionStrip label={`Actions for ${r.name}`} items={r.invited && !r.archived
                              // N2g — a pending link can be WITHDRAWN (the phone's swipe): pending → ended, the row then Archived.
                              ? [{
                                key: "withdraw", label: withdrawing === r.linkId ? "Withdrawing…" : "Withdraw", destructive: true,
                                disabled: withdrawing !== null,
                                onClick: () => {
                                  if (!window.confirm(WITHDRAW_INVITATION_WARNING)) return;
                                  setWithdrawing(r.linkId); setWithdrawError(null);
                                  void endCoaching(supabase as unknown as UpdatableClient, [r.linkId]).then((res) => {
                                    setWithdrawing(null);
                                    if (res.error !== null) { setWithdrawError(res.error); return; }
                                    setReloadKey((k) => k + 1);
                                  });
                                },
                              }]
                              : r.clientId === null ? []
                              : r.archived
                              // N2g — an archived row is read, never written to (D-V8.2: an ended relationship keeps reading, nobody sends).
                              ? [{ key: "open", label: "Open", href: href({ name: "client", clientId: r.clientId }) }]
                              : [
                              { key: "message", label: "Message", href: href({ name: "thread", clientId: r.clientId }) },
                              { key: "assign", label: "Assign", href: `${href({ name: "client", clientId: r.clientId })}?tab=training` },
                              { key: "open", label: "Open", href: href({ name: "client", clientId: r.clientId }) },
                            ]} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {withdrawError !== null && <p className="signin-form-error" role="alert">{withdrawError}</p>}
            {rows.length > 0 && <p className="ours-foot">{rosterFoot(rows)}</p>}
          </>
        )}
        {/* The reviewer seat (2026-10-10): clients who invited this account to REVIEW — apart from his own, and absent
            unless one exists. */}
        <ReviewingGroup />
      </main>
    </Workspace>
  );
}
